import { useNavigate } from "react-router-dom";
import { Heart, Zap, Star, Check, Repeat } from "lucide-react";
import { imgUrl } from "@/lib/api";
import StarRating from "@/components/StarRating";
import { usePicks } from "@/context/PicksContext";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { isInfluencer, isPicked, addPick } = usePicks();
  const picked = isPicked?.(product.id);
  const onSale = product.sale_price && product.sale_price < product.price;
  const isNew = product.condition === "new";

  return (
    <div
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(`/product/${product.id}`)}
      className="bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:border-[#E8956B]/60 hover:shadow-[0_14px_34px_rgba(0,0,0,0.08)] transition-all duration-300 group relative flex flex-col cursor-pointer"
    >
      <div className="relative aspect-square overflow-hidden bg-neutral-100">
        {onSale ? (
          <span className="absolute top-3 left-3 z-10 bg-[#E8956B] text-white text-[10px] font-bold px-2 py-1 rounded tracking-wider">SALE</span>
        ) : isNew ? (
          <span className="absolute top-3 left-3 z-10 bg-[#E8956B] text-white text-[10px] font-bold px-2 py-1 rounded tracking-wider">NEW</span>
        ) : product.condition === "swatched" ? (
          <span className="absolute top-3 left-3 z-10 bg-[#CE744A] text-white text-[10px] font-bold px-2 py-1 rounded tracking-wider">SWATCHED ONCE</span>
        ) : (
          <span className="absolute top-3 left-3 z-10 bg-neutral-800 text-white text-[10px] font-bold px-2 py-1 rounded tracking-wider">LIGHTLY USED</span>
        )}
        {product.status === "sold" && (
          <span className="absolute top-3 right-3 z-10 bg-neutral-900/85 text-white text-[10px] font-bold px-2 py-1 rounded tracking-wider">SOLD</span>
        )}
        {product.status === "in_trade" && (
          <span data-testid={`in-trade-badge-${product.id}`} className="absolute top-3 right-3 z-10 bg-amber-500/90 text-white text-[10px] font-bold px-2 py-1 rounded tracking-wider">IN THE PROCESS</span>
        )}
        {product.status === "traded" && (
          <span className="absolute top-3 right-3 z-10 bg-neutral-900/85 text-white text-[10px] font-bold px-2 py-1 rounded tracking-wider">TRADED</span>
        )}
        {product.boosted && !["sold", "in_trade", "traded"].includes(product.status) && (
          <span data-testid={`boosted-badge-${product.id}`} className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-white/90 backdrop-blur text-[#CE744A] text-[10px] font-bold px-2 py-1 rounded-full tracking-wider shadow-sm">
            <Zap size={11} className="fill-[#E8956B] text-[#E8956B]" /> BOOSTED
          </span>
        )}
        <img
          src={imgUrl(product.images?.[0])}
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-neutral-900 text-sm leading-tight line-clamp-2">{product.title}</h3>
        <p className="text-xs text-neutral-500 mt-1">{product.brand}</p>
        {product.seller_name && (
          <button
            data-testid={`product-seller-link-${product.id}`}
            onClick={(e) => { e.stopPropagation(); navigate(`/seller/${product.seller_id}`); }}
            className="text-[11px] text-neutral-400 hover:text-[#E8956B] hover:underline text-left mt-0.5 w-fit transition-colors"
          >
            by {product.seller_name}
          </button>
        )}
        {product.review_count > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <StarRating value={product.avg_rating} size={12} />
            <span className="text-[11px] text-neutral-400">({product.review_count})</span>
          </div>
        )}
        {product.seller_trade_review_count > 0 && (
          <div data-testid={`trade-rating-${product.id}`} className="flex items-center gap-1 mt-1">
            <Repeat size={11} className="text-[#E8956B]" />
            <span className="text-[11px] font-semibold text-neutral-600">{product.seller_trade_rating.toFixed(1)}</span>
            <span className="text-[11px] text-neutral-400">· {product.seller_trade_review_count} trade{product.seller_trade_review_count > 1 ? "s" : ""}</span>
          </div>
        )}
        <div className="mt-2 flex items-center gap-2">
          {onSale ? (
            <>
              <span className="text-lg font-bold text-neutral-900">${product.sale_price.toFixed(2)}</span>
              <span className="text-sm text-neutral-400 line-through">${product.price.toFixed(2)}</span>
            </>
          ) : (
            <span className="text-lg font-bold text-neutral-900">${product.price.toFixed(2)}</span>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            data-testid={`buy-now-${product.id}`}
            onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
            className="flex-1 border-2 border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B] hover:text-white font-bold rounded-full py-2 text-sm transition-all"
          >
            {product.status === "sold" ? "View" : "Buy Now"}
          </button>
          <button
            onClick={(e) => e.stopPropagation()}
            className="w-9 h-9 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-[#E8956B] hover:border-[#E8956B] transition-colors"
          >
            <Heart size={16} />
          </button>
        </div>
        {isInfluencer && product.status !== "sold" && (
          <button
            data-testid={`add-pick-${product.id}`}
            disabled={picked}
            onClick={(e) => { e.stopPropagation(); if (!picked) addPick(product.id); }}
            className={`mt-2 w-full flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-bold transition-all ${picked ? "bg-[#E8956B]/10 text-[#CE744A] cursor-default" : "border border-[#E8956B] text-[#E8956B] hover:bg-[#E8956B]/10"}`}
          >
            {picked ? <><Check size={13} /> In your picks</> : <><Star size={13} /> Add to my picks</>}
          </button>
        )}
      </div>
    </div>
  );
}
